import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  LinearProgress,
  FormControl,
  FormLabel,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Upload as UploadIcon,
  HowToReg as HowToRegIcon
} from '@mui/icons-material';
import { partnerProductService } from '../../services/apiServices';
import { sanitizeInput } from '../../utils/validationUtils';
import { getClientPartners } from '../services/fundedhereService';

const PartnerProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null); // For edit
  const [dialogMode, setDialogMode] = useState('edit'); // Only 'edit' mode now

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Snackbar for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // File upload states
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Client ID filter state
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [clientPartners, setClientPartners] = useState([]);

  // Form data for create/edit dialog
  const [formData, setFormData] = useState({
    clientId: '',
    warehouseId: '',
    productId: '',
    productName: '',
    unitPrice: 0,
    status: 1, // Default to active
    registered: 0, // Default to not registered
  });

  // --- Effects ---

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Fetch client partners for filter dropdown
  useEffect(() => {
    const fetchClientPartners = async () => {
      try {
        const response = await getClientPartners({
          page: 0,
          pageSize: 1000, // Get all partners in one request
        });

        // Handle response based on its structure - check if response.data is an array
        // or if the actual array is nested within response.data
        let partnersData = [];

        if (Array.isArray(response)) {
          // If response is directly an array
          partnersData = response;
        } else if (Array.isArray(response.data)) {
          // If response.data is an array
          partnersData = response.data;
        } else if (response.data && response.data.data) {
          // If response.data has a nested data property (e.g., { data: [...], metadata: {...} })
          partnersData = response.data.data || [];
        } else if (response.data && response.data.content) {
          // If response.data has a content property (e.g., Spring Data format: { content: [...], totalElements: 0, ... })
          partnersData = response.data.content || [];
        } else if (response.data && typeof response.data === 'object') {
          // If response.data is an object that might contain the array under a different property
          // Try common properties
          if (response.data.content && Array.isArray(response.data.content)) {
            partnersData = response.data.content;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            partnersData = response.data.items;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            partnersData = response.data.results;
          } else {
            // If none of the above patterns match, try to get any array property from the data
            const dataKeys = Object.keys(response.data);
            for (const key of dataKeys) {
              if (Array.isArray(response.data[key])) {
                partnersData = response.data[key];
                break;
              }
            }
            // Last resort: if response.data itself is an array (though caught above, just to be sure)
            if (partnersData.length === 0 && Array.isArray(response.data)) {
              partnersData = response.data;
            }
          }
        } else {
          // Fallback to empty array
          partnersData = [];
        }

        setClientPartners(partnersData);
      } catch (err) {
        console.error('Failed to fetch client partners:', err);
        setClientPartners([]); // Ensure clientPartners is always an array
        // Optionally show error to user
      }
    };

    fetchClientPartners();
  }, []);

  // Fetch products based on search and pagination
  const fetchProducts = useCallback(async (paramsOverride = null) => {
    const params = paramsOverride || {
      page: page,
      size: rowsPerPage,
    };
    if (!paramsOverride && debouncedSearchQuery) {
      params.search = debouncedSearchQuery;
    }

    setLoading(true);
    setError(null);
    try {
      let response;
      if (clientIdFilter) {
        // Use the specific client ID endpoint when client ID filter is applied
        response = await partnerProductService.getPartnerProductsByClientId(clientIdFilter, params);
      } else {
        // Use the general endpoint when no client ID filter is applied
        response = await partnerProductService.getPartnerProducts(params);
      }

      // Handle response data based on its structure
      let productsData = [];
      let totalElementsCount = 0;

      if (Array.isArray(response)) {
        // If response is directly an array
        productsData = response;
        totalElementsCount = response.length;
      } else if (Array.isArray(response.data)) {
        // If response.data is an array
        productsData = response.data;
        totalElementsCount = response.totalElements || response.totalElements || productsData.length;
      } else if (response.data && response.data.data) {
        // If response has nested data property like { data: [...], totalElements: 0 }
        productsData = response.data.data || [];
        totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.data.length || 0;
      } else if (response.data && response.data.content) {
        // If response has content property like Spring Data format
        productsData = response.data.content || [];
        totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.content.length || 0;
      } else if (response.data && typeof response.data === 'object') {
        // Try to find array in response.data object
        if (response.data.content && Array.isArray(response.data.content)) {
          productsData = response.data.content;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.content.length || 0;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          productsData = response.data.items;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.items.length || 0;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          productsData = response.data.results;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.results.length || 0;
        } else {
          // Try to find any array property in the response.data object
          const dataKeys = Object.keys(response.data);
          for (const key of dataKeys) {
            if (Array.isArray(response.data[key])) {
              productsData = response.data[key];
              totalElementsCount = response.data.totalElements || response.data.totalElements || response.data[key].length || 0;
              break;
            }
          }
          if (productsData.length === 0 && Array.isArray(response.data)) {
            productsData = response.data;
            totalElementsCount = productsData.length;
          }
        }
      } else {
        productsData = Array.isArray(response) ? response : (response.data || []);
        totalElementsCount = productsData.length;
      }

      setProducts(productsData);
      setTotalElements(totalElementsCount);
    } catch (err) {
      console.error('Failed to load partner products:', err);
      setError('Failed to load partner products.');
      setSnackbarMessage('Failed to load partner products.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchQuery, clientIdFilter, setProducts, setLoading, setError, setSnackbarMessage, setSnackbarSeverity, setSnackbarOpen]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // --- Handlers ---

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };


  const handleEditClick = (product) => {
    setDialogMode('edit');
    setCurrentProduct(product);
    setFormData({
      clientId: product.clientId,
      warehouseId: product.warehouseId,
      productId: product.productId,
      productName: product.productName,
      unitPrice: product.unitPrice,
      status: product.status,
      registered: product.registered || 0,
    });
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setCurrentProduct(null);
    setError(null); // Clear dialog-specific errors
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Set loading for form submission
    try {
      // Only edit mode remains after removing create functionality
      await partnerProductService.updatePartnerProduct(currentProduct.id, formData);
      setSnackbarMessage('Product updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleDialogClose();
      // Re-fetch products to update the list with current pagination, search, and client filter
      fetchProducts();
    } catch (err) {
      setError(err.message || 'An error occurred during submission.');
      setSnackbarMessage(err.message || 'Submission failed.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    setLoading(true);
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await partnerProductService.togglePartnerProductStatus(id, newStatus);
      setSnackbarMessage('Product status updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      // Re-fetch products to update the list with current pagination, search, and client filter
      fetchProducts();
    } catch (err) {
      setError(err.message || 'Failed to toggle product status.');
      setSnackbarMessage(err.message || 'Failed to toggle product status.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when rows per page changes
  };

  const handleUploadClick = () => {
    setOpenUploadDialog(true);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (file.type === 'text/csv' || file.name.endsWith('.csv') || 
          file.type.includes('excel') || file.name.endsWith('.xlsx')) {
        setSelectedFile(file);
      } else {
        setSnackbarMessage('Please select a CSV or Excel file.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setSnackbarMessage('Please select a file first.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Create FormData object to send the file
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Use your existing API service which should handle authentication properly
      await partnerProductService.uploadPartnerProductsFile(formData);

      setSnackbarMessage('File uploaded successfully! Processing may take a few moments.');
      setSnackbarSeverity('success');

      // Close dialog and refresh the product list
      handleUploadDialogClose();

      // Refresh the product list after a delay to allow for background processing
      setTimeout(() => {
        fetchProducts();
      }, 5000);
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Upload failed. Please try again.';
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || error.response.data?.error || `${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Network error. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = error.message || errorMessage;
      }
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
    }

    setSnackbarOpen(true);
  };

  const handleRegisterProduct = async (product) => {
    setLoading(true);
    try {
      // registerPartnerProduct handles:
      // 1. Fetch client email by clientId using getClientPartnersByClientId
      // 2. POST to /fundedhere/inventory with product details
      //    Expected payload: { email, items: [{ upcId, name, productName, unitPrice, location }] }
      // 3. PUT /partner-products/{id} to set registered = 1
      const updatedProduct = await partnerProductService.registerPartnerProduct(product.id, product);

      // Update the local product list to reflect the registration status
      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === product.id ? { ...p, ...updatedProduct } : p
        )
      );

      setSnackbarMessage('Product registered successfully in FundedHere inventory!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Registration failed. Please try again.';
      if (error.response) {
        // Check for specific error messages from either FundedHere or our backend
        if (error.response.status === 400) {
          errorMessage = error.response.data?.message || 'Invalid product data. Check email, UPC ID, and location.';
        } else if (error.response.status === 401) {
          errorMessage = 'Invalid API key for FundedHere API.';
        } else if (error.response.status === 409) {
          errorMessage = 'Product already registered in FundedHere inventory.';
        } else if (error.response.status === 500) {
          errorMessage = 'FundedHere server error. Please try again later.';
        } else {
          errorMessage = error.response.data?.message || error.response.data?.error || `${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and FundedHere API availability.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Partner Products
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<UploadIcon />}
            onClick={handleUploadClick}
          >
            Bulk Upload
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <TextField
            label="Search Product Name/ID"
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{ flexGrow: 1, maxWidth: 300 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <IconButton onClick={handleClearSearch} edge="end" size="small">
                      <ClearIcon />
                    </IconButton>
                  )}
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Filter by Client ID"
            select
            value={clientIdFilter}
            onChange={(e) => setClientIdFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">
              <em>All Clients</em>
            </MenuItem>
            {clientPartners.map((partner) => (
              <MenuItem key={partner.clientId} value={partner.clientId}>
                {partner.clientId}
              </MenuItem>
            ))}
          </TextField>
          {clientIdFilter && (
            <Button
              onClick={() => setClientIdFilter('')}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ height: '40px', alignSelf: 'flex-end' }}
            >
              Clear Filter
            </Button>
          )}
        </Box>
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && products.length === 0 && (
        <Alert severity="info">No partner products found.</Alert>
      )}
      {!loading && !error && products.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="partner products table">
              <TableHead>
                <TableRow>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Warehouse ID</TableCell>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Registration</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow
                    key={product.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {sanitizeInput(product.clientId)}
                    </TableCell>
                    <TableCell>{sanitizeInput(product.warehouseId)}</TableCell>
                    <TableCell>{sanitizeInput(product.productId)}</TableCell>
                    <TableCell>{sanitizeInput(product.productName)}</TableCell>
                    <TableCell align="right">{sanitizeInput(product.unitPrice)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color={product.status === 1 ? 'success' : 'error'}
                        onClick={() => handleToggleStatus(product.id, product.status)}
                        aria-label={product.status === 1 ? 'deactivate product' : 'activate product'}
                      >
                        {product.status === 1 ? <ToggleOnIcon fontSize="large" /> : <ToggleOffIcon fontSize="large" />}
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      {product.registered === 1 ? (
                        <Typography variant="body2" color="success.main">Registered</Typography>
                      ) : (
                        <Tooltip title="Register Product">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleRegisterProduct(product)}
                            disabled={loading}
                            aria-label="register product"
                          >
                            <HowToRegIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(product)}
                        aria-label="edit product"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Create/Edit Product Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Edit Product</DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent dividers>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              autoFocus
              margin="dense"
              name="clientId"
              label="Client ID"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(formData.clientId)}
              onChange={handleFormChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="warehouseId"
              label="Warehouse ID"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(formData.warehouseId)}
              onChange={handleFormChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="productId"
              label="Product ID"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(formData.productId)}
              onChange={handleFormChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="productName"
              label="Product Name"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(formData.productName)}
              onChange={handleFormChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="unitPrice"
              label="Unit Price"
              type="number"
              fullWidth
              variant="outlined"
              value={sanitizeInput(formData.unitPrice)}
              onChange={handleFormChange}
              required
              inputProps={{ min: 0 }}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status === 1}
                  onChange={(e) => handleFormChange({
                    target: {
                      name: 'status',
                      type: 'checkbox',
                      checked: e.target.checked
                    }
                  })}
                  name="status"
                  color="primary"
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.registered === 1}
                  onChange={(e) => handleFormChange({
                    target: {
                      name: 'registered',
                      type: 'checkbox',
                      checked: e.target.checked
                    }
                  })}
                  name="registered"
                  color="primary"
                />
              }
              label="Registered"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="secondary">
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* File Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={handleUploadDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Bulk Product Upload</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 2, mb: 2 }}>
            <FormControl fullWidth required>
              <FormLabel>Upload File</FormLabel>
              <input
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                id="upload-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="upload-file">
                <Button 
                  variant="outlined" 
                  component="span"
                  fullWidth
                  sx={{ mt: 1, mb: 2 }}
                >
                  Select CSV or Excel File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
            </FormControl>
            
            {isUploading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Uploading...</Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Please prepare your CSV/Excel file with the following columns: 
                clientId, warehouseId, productId, productName, unitPrice, status.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleFileUpload} 
            color="primary" 
            variant="contained" 
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? <CircularProgress size={24} /> : 'Upload File'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerProducts;